/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
/// 💬 Globals Store - State Used Across the Entire Application
/// ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { AlertSnackbar } from '@/types/typesGlobal'

export const useGlobalsStore = defineStore('globalsStore', () => {
  // //////////////////////////////////////////////////////////////////
  // 💬 System Globals
  // //////////////////////////////////////////////////////////////////
  const applicationName = ref('Pulsar')
  const applicationOfficialName = ref('PulsarAI')
  const applicationDomain = ref(process.env.NODE_ENV === 'development' ? 'http://localhost:3000' : 'https://app.pulsarai.ai')
  const productName = ref('Snowflake')

  // 💬 Timezone Related State
  // ///////////////////////////////////////////////////////////////////
  const userTimezone = ref('America/Denver') // Users Current Timezone State

  // const applicationDomainLocalEmulation = ref('http://localhost:3000')
  const docStore = useDocStore()
  const { getDoc } = docStore

  // //////////////////////////////////////////////////////////////////
  // 💬 Global Layouts
  // //////////////////////////////////////////////////////////////////
  const railCollapsedState = ref(true)

  // //////////////////////////////////////////////////////////////////
  // 💬 Account Profile Information
  // //////////////////////////////////////////////////////////////////

  // Note* Account and User Docs are Synced in the docStore.docs object
  const accountProfileDocSync = ref({} as any)
  const accountId = ref(null as string | null)
  const userId = ref(null as string | null)
  const accountNamespace = ref(null as string | null) // Default Snowflake Namespace

  const syncAccountProfile = (account: string) => {
    accountId.value = account

    getDoc('accountProfile', 'accounts', account, true, accountProfileDocSync)
  }

  const accountProfile = computed(() => {
    // 💬 Get Snowflake System Namespace
    // /////////////////////////////////
    const systemDatabase = accountProfileDocSync.value?.snowflakeSettings?.systemOwnership?.database || ''
    const systemSchema = accountProfileDocSync.value?.snowflakeSettings?.systemOwnership?.schema || ''

    accountNamespace.value = systemDatabase ? `${systemDatabase}.${systemSchema}` : null

    return accountProfileDocSync.value
  })

  // 💬 User Profile Information
  // /////////////////////////////////
  const userProfileDocSync = ref({} as any)

  const syncUserProfile = (account: string, uid: string) => {
    getDoc('userProfile', 'users', uid, true, userProfileDocSync)
  }

  const userProfile = computed(() => {
    const { firstName, lastName, snowflakeConnections, defaults } = userProfileDocSync.value
    const fullName = firstName && lastName ? `${firstName} ${lastName}` : ''
    const initials = firstName && lastName ? `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}` : ''

    // Get Active Snowflake Connection from Defaults if Available
    let activeSnowflakeConnection = snowflakeConnections && defaults?.snowflakeConnection ? snowflakeConnections[defaults.snowflakeConnection] : null

    // If Not Using a Snowflake Connection, Set Active Snowflake Connection to Null
    if (activeSnowflakeConnection && activeSnowflakeConnection.connectionType === 'none') activeSnowflakeConnection = null

    return { ...userProfileDocSync.value, fullName, initials, activeSnowflakeConnection }
  })

  // //////////////////////////////////////////////////////////////////
  // 💬 Application Theme
  // //////////////////////////////////////////////////////////////////
  // "theme" has a cookie for remembering the user's theme preference
  const theme = ref('light' as 'light' | 'dark' | 'system') // app.vue will check for an active cookie
  const themeCookie = useCookie('theme')

  function toggleTheme() {
    theme.value = theme.value === 'dark' ? 'light' : 'dark'
    themeCookie.value = theme.value
  }

  // //////////////////////////////////////////////////////////////////
  // 💬 Dashboard Filters
  // //////////////////////////////////////////////////////////////////


  // //////////////////////////////////////////////////////////////////
  // 💬 Throw Global Snackbar Alert
  // //////////////////////////////////////////////////////////////////
  const alertSnackbarState = ref<AlertSnackbar>({ visible: false, color: 'info', message: undefined, timeout: 3000 })

  const alertSnackbar = (message: string, color: 'info' | 'success' | 'warning' | 'error' = 'info', timeout: number = 3000) => {
    if (timeout === 0) timeout = -1 // -1 means the snackbar will not close automatically
    alertSnackbarState.value = { visible: true, color, message, timeout }
  }

  return {
    theme,
    applicationName,
    applicationOfficialName,
    applicationDomain,
    userTimezone,
    railCollapsedState,
    accountId,
    userId,
    accountProfile,
    accountNamespace,
    userProfile,
    alertSnackbarState,
    alertSnackbar,
    toggleTheme,
    syncAccountProfile,
    syncUserProfile,
  }
})
